/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

/* LA NEWSLETTER AUTOMATIQUE D'UN CLIENT ( Fred, 19-21/10/2026 ).

   Les formes sont celles de l'API ( `console/newsletter/*`, reponse back du
   21/10/2026 ), gardees telles quelles, noms compris : le relais de la Console
   ne les serialise pas ( `Internal/Newsletters` ), et le Desktop lit les memes. */

export type NewsletterFrequencyKind = "weekly" | "monthly_first";

export interface NewsletterFrequency {
    kind: NewsletterFrequencyKind;
    /* 1 lundi … 7 dimanche. */
    weekday: number;
    every: number;
    /* « HH:MM », dans le fuseau du compte. */
    time: string;
}

/* Les destinataires, format de `campaign.search`. Inclusion en OU, exclusion par
   `*_out`. Sans critere d'inclusion : personne. */
export interface NewsletterSearch {
    tags_in?: number[];
    tags_out?: number[];
    contacts_in?: number[];
    contacts_out?: number[];
    /* 1 proprietaire, 2 locataire, 3 ancien proprietaire, 4 ancien locataire. */
    roles_in?: number[];
    active_buyers?: boolean;
    active_renters?: boolean;
    owners_also_buyers?: boolean;
    all_contacts?: boolean;
}

export interface Newsletter {
    id: number;
    name: string;
    subject: string | null;
    sender_name: string | null;
    sender_email: string | null;
    reply_to_email: string | null;
    utm_campaign: string | null;
    search: NewsletterSearch;
    frequency: NewsletterFrequency;
    next_send_at: string | null;
    active: boolean;
    /* L'e-mail porte au moins un bloc rempli a l'envoi ( articles, annonces,
       vendues ) : sinon l'activation est refusee ( 422 `no_dynamic_block` ). */
    has_dynamic_block: boolean;
    created_at: string | null;
    updated_at: string | null;
    updated_by: { user_id: number | null; console: boolean };
    /* L'e-mail ( document du PB e-mail, en JSON ). ABSENT tant que l'API Console ne le
       rend pas ( brief du 26/10/2026 ) : la Console ne propose alors pas de l'ouvrir. */
    content_editor?: string | null;
}

/* Ce que la Console ecrit. L'e-mail ( `content_editor` ) depuis le 26/10/2026. */
export interface NewsletterFields {
    content_editor?: string;
    name?: string;
    subject?: string;
    sender_name?: string;
    sender_email?: string;
    reply_to_email?: string;
    search?: NewsletterSearch;
    frequency?: NewsletterFrequency;
    next_send_at?: string;
    active?: boolean;
}

export interface NewsletterCount {
    matched: number;
    total: number;
    excluded: { no_email: number; not_authorized: number; bounced: number };
}

export interface NewsletterSendingDomain {
    id: number;
    domain: string;
    valid: boolean;
}

export interface NewsletterTag {
    id: number;
    name: string;
    color: string | null;
}

export interface NewsletterContact {
    id: number;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
}

export interface NewsletterBlog {
    id: number;
    name: string;
    url_feed: string | null;
    status: unknown;
    imported_at: string | null;
    /* Demandes au back le 26/10/2026 : absents tant qu'ils ne sont pas livres. */
    start_date?: string | null;
    cta_script_url?: string | null;
}

export interface NewsletterPreviewPost {
    id: number;
    title: string;
    url?: string;
    /* La vignette de l'article ( `presentPost` du back ), affichee dans l'apercu. */
    picture?: string | null;
    published_at?: string;
    /* Le ciblage de l'article ; vide = il part a tous. */
    targets?: { [ key: string ]: unknown };
    /* Les tags de l'article ( reponse back du 26/10/2026 ). */
    tags?: { id: number; name: string; color?: string | null }[];
}

export interface NewsletterPreview {
    /* Le sujet du prochain envoi, variables de date remplacees ( 26/10/2026 ). */
    subject?: string;
    since: string | null;
    recipients: number;
    with_articles: number;
    blocks: {
        key: string;
        total: number;
        blog_rss_ids: number[];
        tag_ids: number[];
        candidates: NewsletterPreviewPost[];
    }[];
    listings?: unknown;
    contact?: { id: number; blocks: unknown };
}

export interface NewsletterSummary {
    period: { from: string; to: string };
    sends: {
        done: number;
        skipped_no_article: number;
        skipped_other: number;
        failed: number;
        next_send_at: string | null;
    };
    last_send: {
        id: number;
        date: string;
        targeted: number;
        excluded: { no_email: number; not_authorized: number; bounced: number };
        recipients: number;
    } | null;
    emails: { sent: number; delivered: number; bounced: number };
    opens: { unique: number; rate: number | null };
    clicks: { unique: number; rate: number | null; click_to_open: number | null };
    unsubscribed: number;
    spam_reports: number;
    blog_visits: number | null;
    leads: number | null;
    curve: {
        send_id: number;
        date: string;
        open_rate: number | null;
        click_rate: number | null;
        unsubscribed: number;
    }[];
}

export interface NewsletterSendMetrics {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    unsubscribed: number;
    spam_reports: number;
    open_rate: number | null;
    click_rate: number | null;
}

export type NewsletterSendStatus =
    "scheduled" | "skipped_no_article" | "skipped_no_recipient" | "skipped_no_sender" | "failed";

export interface NewsletterSend {
    id: number;
    status: NewsletterSendStatus;
    date: string;
    scheduled_for: string | null;
    since: string | null;
    subject: string | null;
    posts_count: number;
    targeted: number;
    recipients_count: number;
    campaign_id: number | null;
    error: string | null;
    metrics: NewsletterSendMetrics | null;
}

export interface NewsletterSendRecipients {
    contacts: {
        contact_id: number;
        firstname: string | null;
        lastname: string | null;
        email: string | null;
        posts_received: number[];
        sent: boolean;
        delivered: boolean;
        opened: boolean;
        clicked: boolean;
        bounced: boolean;
        posts_clicked: number[];
    }[];
    posts: { id: number; title: string; url: string; recipients: number; clicks: number }[];
    total?: number;
}

export interface NewsletterPostStats {
    id: number;
    title: string;
    url?: string;
    times_sent: number;
    contacts_reached: number;
    clicks: number;
    blog_visits: number | null;
    /* DEMANDES AU BACK le 22/10/2026 ( brief « lot 6 et droits des roles » ) : les
       contacts qui ont OUVERT un e-mail contenant l'article, et sa place moyenne
       dans l'e-mail ( 1 = en tete ). Absents tant que le back ne les rend pas. */
    opened?: number | null;
    average_position?: number | null;
}

/* Un client qui a l'offre, activee ou non ( `console/features/newsletter`, demandee
   au back le 21/10/2026 ). */
export interface NewsletterOffer {
    group_id: number;
    group_name: string | null;
    company: string | null;
    owner_email: string | null;
    enabled: boolean;
    updated_at: string | null;
    updated_by: string | null;
    newsletters: number;
    active_newsletters: number;
    next_send_at: string | null;
}

export const NEWSLETTER_FEATURE = "newsletter";
